/**
 * RFC-0089: Yjs-backed canvas state hooks.
 * RFC-0319: Per-field YDoc node data (source of truth).
 *
 * These hooks observe a Y document's "nodes", "edges", and "viewport" maps
 * and convert them to ReactFlow-compatible data structures. They also provide
 * write functions for updating positions and viewport back into the Y doc.
 *
 * The backend computes node positions (via layout.py) and writes them to YDoc.
 * The frontend reads positions directly from YDoc — no client-side layout needed.
 * The frontend writes only x, y, tainted (on drag) and viewport (on pan/zoom).
 *
 * Per-field reads (RFC-0319): individual in_ and out_ prefixed keys are read
 * directly from each Y.Map node entry with schema-driven deserialization.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import * as Y from 'yjs'
import type { Edge, Node, Viewport } from 'reactflow'
import { CANVAS_HIDDEN_FE_TYPES, FE_TYPE_TO_BACKEND, YDOC_DESERIALIZERS, YDOC_NODE_FIELDS } from '../generated/nodeDataTypes'
import { getNodeDimensions } from '../components/canvas-v2/nodeRegistry'
import {
  computeChildPosition,
  CHILD_LABEL_HEIGHT,
  CHILD_WIDTH,
  CHILD_HEIGHT,
  type LayoutMode,
} from '../components/canvas-v2/nodes/GroupNode/GroupNode'
import { createDebug } from '../utils/debugLog'

const log = createDebug('dw:canvas:ydoc')
const statusLog = createDebug('dw:canvas:status')
const ydocWriteLog = createDebug('dw:ydoc:write')
const lifecycleLog = createDebug('dw:ydoc:lifecycle')
const layoutLog = createDebug('dw:layout:position')

// ---------------------------------------------------------------------------
// RFC-0319: YDoc structural keys (NOT in_*/out_* data fields)
// ---------------------------------------------------------------------------
export const YDOC_STRUCTURAL_KEYS = new Set([
  'type', 'status', 'label',
  'x', 'y', 'tainted', 'width', 'height',
  'parentNode',
])

// Legacy field renames: old internal field names → new in_* names.
// Applied during read so old YDoc data is transparently migrated.
const _YDOC_FIELD_RENAMES: Record<string, string> = {
  resolved_voice_map: 'in_resolved_voice_map',
  generated_audio: 'in_generated_audio',
  rendered_clip_paths: 'out_rendered_clip_paths',
  in_rendered_clip_paths: 'out_rendered_clip_paths',
  review_history: 'in_review_history',
}

/**
 * Read node data from a Y.Map entry using per-field keys.
 *
 * RFC-0319: All non-structural keys are read as node data fields.
 * This includes in_/out_ prefixed fields and internal fields
 * (e.g. batch_pending, in_resolved_voice_map).
 * Deserialization is schema-driven via generated YDOC_DESERIALIZERS.
 */
export function readNodeDataFromYMap(ynode: Y.Map<unknown>): Record<string, unknown> {
  const dataRaw: Record<string, unknown> = {}

  // Look up schema-driven deserializers for this node type
  const feType = (ynode.get('type') as string) || ''
  const desers = YDOC_DESERIALIZERS[toBackendType(feType)] ?? {}

  // Read all non-structural keys (in_*, out_*, and internal fields)
  for (const key of ynode.keys()) {
    if (YDOC_STRUCTURAL_KEYS.has(key)) continue
    const raw = ynode.get(key)
    // Migrate legacy field names to new in_* names
    const newKey = _YDOC_FIELD_RENAMES[key]
    if (newKey && !(newKey in dataRaw)) {
      const deser = desers[newKey]
      dataRaw[newKey] = deser ? deser(raw) : raw
      continue
    }
    const deser = desers[key]
    dataRaw[key] = deser ? deser(raw) : raw
  }

  const statusVal = ynode.get('status') as string | undefined
  statusLog('read nodeType=%s fieldCount=%d status=%s', feType, Object.keys(dataRaw).length, statusVal ?? 'undefined')

  return dataRaw
}

/**
 * Serialize a field value for YDoc per-field storage.
 * Scalars stored as primitives; complex values JSON-stringified.
 */
function serializeFieldValue(value: unknown): unknown {
  if (value === null || value === undefined) return value
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
  try { return JSON.stringify(value) } catch { return String(value) }
}

/** Convert frontend camelCase node type to backend snake_case.
 *
 * Uses the generated FE_TYPE_TO_BACKEND lookup table (from schemas) with a
 * regex fallback for any types not in the table. The lookup is needed because
 * naive camelCase→snake_case doesn't work for all types.
 */
export function toBackendType(feType: string): string {
  return FE_TYPE_TO_BACKEND[feType] ?? feType.replace(/([A-Z])/g, '_$1').toLowerCase()
}

/**
 * Compute the cell size for a group's children by reading types from YDoc.
 * Mirrors GroupNode.computeCellSize but reads node types from Y.Map instead
 * of ReactFlow nodes (since ReactFlow state may not exist during readNodesFromYDoc).
 */
function computeCellSizeFromYDoc(
  childIds: string[],
  yNodes: Y.Map<Y.Map<unknown>>,
): { cellWidth: number; cellHeight: number } {
  const actualIds = childIds.filter((id): id is string => id !== null)
  if (actualIds.length === 0) {
    return { cellWidth: CHILD_WIDTH, cellHeight: CHILD_HEIGHT }
  }

  let maxWidth = 0
  let maxHeight = 0

  for (const childId of actualIds) {
    const childYNode = yNodes.get(childId)
    if (!childYNode) continue
    const childType = (childYNode.get('type') as string) || ''
    if (!childType) continue
    const dims = getNodeDimensions(childType)
    if (dims.width > maxWidth) maxWidth = dims.width
    const fullHeight = dims.height + CHILD_LABEL_HEIGHT + dims.footerHeight
    if (fullHeight > maxHeight) maxHeight = fullHeight
  }

  if (maxWidth === 0 || maxHeight === 0) {
    return { cellWidth: CHILD_WIDTH, cellHeight: CHILD_HEIGHT }
  }

  return { cellWidth: maxWidth, cellHeight: maxHeight }
}

/**
 * Node as stored in the Y document (backend-computed position included).
 */
export interface BackendNode {
  id: string
  type: string
  data: {
    status: string
    nodeType?: string
    [key: string]: unknown
  }
  position: { x: number; y: number }
}

// ---------------------------------------------------------------------------
// useYCanvas: Y doc → ReactFlow nodes/edges (single observer)
// ---------------------------------------------------------------------------

interface UseYCanvasOptions {
  ydoc: Y.Doc
  synced: boolean
  /** ReactFlow setNodes */
  setNodes: (nodes: Node[] | ((prev: Node[]) => Node[])) => void
  /** ReactFlow setEdges */
  setEdges: (edges: Edge[] | ((prev: Edge[]) => Edge[])) => void
  /** Ref to current ReactFlow nodes */
  nodesRef: React.MutableRefObject<Node[]>
  /** Ref to current ReactFlow edges */
  edgesRef: React.MutableRefObject<Edge[]>
  /** Whether backend is connected and Y doc is synced */
  isBackendConnected: boolean
  /** Ref to skip rebuild during undo/redo */
  isUndoRedo: React.MutableRefObject<boolean>
  /** ReactFlow instance ref for auto-focus */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reactFlowInstance: React.MutableRefObject<{ fitView: (opts: any) => void; setCenter: (x: number, y: number, opts?: any) => void } | null>
  /** When set, skip YDoc nodes whose type is not in this set */
  knownNodeTypes?: ReadonlySet<string>
  /** Scalar selection ref — single source of truth for which node is selected */
  selectedNodeIdRef: React.RefObject<string | null>
}

interface UseYCanvasResult {
  /** True after the first rebuild has populated nodes into React state */
  ready: boolean
  /** Number of non-director nodes with status=running in YDoc (includes canvas-hidden nodes) */
  ydocRunningCount: number
  /** Number of non-director nodes with status=pending_execution in YDoc */
  ydocQueuedCount: number
  /** Write a node's position (and optionally tainted flag) back to the Y document */
  writeNodePosition: (nodeId: string, x: number, y: number, tainted?: boolean) => void
  /** Pre-create a node in the Y doc so backend reconciliation preserves its position */
  addNodeToYDoc: (nodeId: string, nodeType: string, label: string, x: number, y: number, data?: Record<string, unknown>, tainted?: boolean) => void
  /** Remove a node from the Y doc (cleanup on deletion) */
  removeNodeFromYDoc: (nodeId: string) => void
  /** Patch per-field in_/out_ keys on a node's YDoc entry */
  writeNodeData: (nodeId: string, updates: Record<string, unknown>) => void
  /** Write measured dimensions back to YDoc so backend layout knows real sizes */
  writeNodeDimensions: (nodeId: string, width: number, height: number) => void
  /** Set or clear the top-level parentNode field on a ynode */
  writeNodeParent: (nodeId: string, parentId: string | null) => void
}

/**
 * Stamp node.selected from the scalar ref. Used during YDoc rebuilds to
 * preserve the user's current selection without scanning prevNodes.
 */
function applySelection(nodes: Node[], selectedIdRef: React.RefObject<string | null>): Node[] {
  const selectedId = selectedIdRef.current
  if (!selectedId) return nodes
  return nodes.map(n => n.id === selectedId ? { ...n, selected: true } : n)
}

export function useYCanvas({
  ydoc,
  synced,
  setNodes,
  setEdges,
  nodesRef,
  edgesRef,
  isBackendConnected,
  isUndoRedo,
  reactFlowInstance,
  knownNodeTypes,
  selectedNodeIdRef,
}: UseYCanvasOptions): UseYCanvasResult {
  // True after the first rebuild has populated nodes — used to gate canvas visibility
  const [ready, setReady] = useState(false)
  // Running non-director node count from YDoc (includes canvas-hidden nodes like storyboardDesigner)
  const [ydocRunningCount, setYdocRunningCount] = useState(0)
  // Queued (pending_execution) non-director node count from YDoc
  const [ydocQueuedCount, setYdocQueuedCount] = useState(0)

  // Keep refs to Y maps for write helpers
  const yNodesRef = useRef<Y.Map<Y.Map<unknown>> | null>(null)

  // Track whether this is the first build (edges deferred by one frame)
  const isInitialBuild = useRef(true)

  // Track previously running nodes for auto-focus
  const prevRunningIdsRef = useRef<Set<string>>(new Set())

  // Debounce auto-focus: accumulate newly running nodes over a window before fitView
  const autoFocusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingFocusNodesRef = useRef<Node[]>([])

  // Explicit focus intents (from backend focus_node tool) take priority over auto-focus
  const lastFocusIntentTs = useRef(0)

  useEffect(() => {
    if (!synced || !isBackendConnected) {
      console.log('[YCanvas] effect skip: synced=%s isBackendConnected=%s', synced, isBackendConnected)
      return
    }
    console.log('[YCanvas] effect run: synced=%s isBackendConnected=%s', synced, isBackendConnected)

    isInitialBuild.current = true

    const yNodes = ydoc.getMap('nodes') as Y.Map<Y.Map<unknown>>
    const yEdges = ydoc.getMap('edges') as Y.Map<Y.Map<unknown>>
    yNodesRef.current = yNodes

    const readNodesFromYDoc = (): Node[] => {
      const nodes: Node[] = []
      yNodes.forEach((ynode: Y.Map<unknown>, nodeId: string) => {
        // RFC-0319: read per-field keys
        const dataRaw = readNodeDataFromYMap(ynode)
        const parentNode = ynode.get('parentNode') as string | undefined
        const nodeType = (ynode.get('type') as string) ?? ''

        // Nodes marked canvasHidden in their schema live in YDoc for data
        // persistence but are never rendered on the canvas.
        if (CANVAS_HIDDEN_FE_TYPES.has(nodeType)) return

        // Skip node types not registered in the caller's schema registry
        if (knownNodeTypes && !knownNodeTypes.has(nodeType)) {
          return
        }

        // Group nodes need explicit dimensions so ReactFlow sizes the container.
        // nodeGroup width is component-driven (GroupNode computes containerWidth from children),
        // so we only pin width from YDoc for backend-managed groups (webtoon/shortVideo).
        const isGroup = nodeType === 'webtoonGroup' || nodeType === 'shortVideoGroup' || nodeType === 'nodeGroup'
        const isBackendManagedGroup = nodeType === 'webtoonGroup' || nodeType === 'shortVideoGroup'
        const collapsed = dataRaw.in_collapsed === true

        // Only set parentNode if the parent exists in YDoc AND will be rendered.
        // A parent that exists in YDoc but is filtered out by knownNodeTypes
        // (e.g. legacy "group" after rename to "nodeGroup") would crash
        // ReactFlow with "Parent node ... not found".
        const parentExists = parentNode ? (() => {
          const parentYNode = yNodes.get(parentNode)
          if (!parentYNode) return false
          const parentType = (parentYNode.get('type') as string) ?? ''
          if (knownNodeTypes && !knownNodeTypes.has(parentType)) return false
          return true
        })() : false

        // For group children, compute position from parent's in_child_node_ids order.
        // This makes in_child_node_ids the SOLE source of truth for child positions,
        // eliminating stale x/y reads and cross-tab sync issues.
        let posX = (ynode.get('x') as number) ?? 0
        let posY = (ynode.get('y') as number) ?? 0

        // Diagnostic logging for designer nodes — traces YDoc position data
        if (['characterDesigner', 'locationDesigner', 'objectDesigner'].includes(nodeType)) {
          layoutLog('[layout] readNodesFromYDoc: %s type=%s x=%d y=%d ydoc_w=%s ydoc_h=%s',
            nodeId, nodeType, posX, posY, ynode.get('width'), ynode.get('height'))
        }

        if (parentExists && parentNode) {
          const parentYNode = yNodes.get(parentNode)
          if (parentYNode) {
            const childIdsRaw = parentYNode.get('in_child_node_ids')
            const childIds = childIdsRaw instanceof Y.Array
              ? (childIdsRaw.toArray() as string[])
              : Array.isArray(childIdsRaw) ? childIdsRaw as string[] : []
            const myIndex = childIds.indexOf(nodeId)
            if (myIndex !== -1) {
              const layoutMode = (parentYNode.get('in_layout_mode') as string) || 'auto'
              const { cellWidth, cellHeight } = computeCellSizeFromYDoc(childIds, yNodes)
              const computed = computeChildPosition(myIndex, layoutMode as LayoutMode, cellWidth, cellHeight)
              posX = computed.x
              posY = computed.y
            }
          }
        }
        const node: Node = {
          id: nodeId,
          type: nodeType,
          position: { x: posX, y: posY },
          data: {
            ...dataRaw,
            status: (ynode.get('status') as string) ?? 'idle',
            label: (ynode.get('label') as string) ?? nodeId,
            tainted: (ynode.get('tainted') as boolean) ?? false,
            ...(parentExists ? { isInsideGroup: true } : {}),
          },
          ...(parentExists ? { parentNode } : {}),
          ...(isGroup ? {
            style: {
              width: isBackendManagedGroup ? ((ynode.get('width') as number) || undefined) : undefined,
              height: collapsed ? 48 : ((ynode.get('height') as number) || undefined),
              padding: 0,
              backgroundColor: 'transparent',
              border: 'none',
            },
          } : {}),
        }

        // Stash the authoritative expanded height so collapse toggle can restore it
        if (isGroup) {
          const ydocHeight = ynode.get('height') as number | undefined
          if (ydocHeight) {
            node.data._expandedHeight = ydocHeight
          }
        }

        // Child nodes inside a collapsed group should be hidden
        // Child nodes inside a shortVideoGroup should be non-draggable (auto-layout)
        if (parentExists) {
          const parentYNode = yNodes.get(parentNode!)
          if (parentYNode) {
            const parentType = parentYNode.get('type') as string | undefined

            // Lock panels in horizontal layout groups (Figma-like auto-layout)
            if (parentType === 'shortVideoGroup') {
              node.draggable = false
            }

            // Hide children of collapsed groups
            if (parentYNode.get('in_collapsed') === true) {
              node.hidden = true
            }
          }
        }

        nodes.push(node)
      })
      // ReactFlow requires parent nodes before children in the array
      nodes.sort((a, b) => {
        if (a.parentNode && !b.parentNode) return 1
        if (!a.parentNode && b.parentNode) return -1
        return 0
      })
      return nodes
    }

    // Build child→collapsedGroup mapping for edge remapping
    const getCollapsedGroupMap = (): Map<string, string> => {
      const childToGroup = new Map<string, string>()
      yNodes.forEach((ynode: Y.Map<unknown>, nodeId: string) => {
        const parentId = ynode.get('parentNode') as string | undefined
        if (!parentId) return
        const parentYNode = yNodes.get(parentId)
        if (!parentYNode) return
        if (parentYNode.get('in_collapsed') === true) {
          childToGroup.set(nodeId, parentId)
        }
      })
      return childToGroup
    }

    const readEdgesFromYDoc = (): Edge[] => {
      const collapsedMap = getCollapsedGroupMap()
      const edges: Edge[] = []
      const seen = new Set<string>()
      yEdges.forEach((yedge: Y.Map<unknown>, edgeId: string) => {
        let source = (yedge.get('source') as string) ?? ''
        let target = (yedge.get('target') as string) ?? ''
        const type = (yedge.get('type') as string) ?? 'default'
        let sourceHandle = yedge.get('sourceHandle') as string | undefined
        let targetHandle = yedge.get('targetHandle') as string | undefined

        // Remap edges involving children of collapsed groups
        if (collapsedMap.has(source)) {
          source = collapsedMap.get(source)!
          sourceHandle = undefined
        }
        if (collapsedMap.has(target)) {
          target = collapsedMap.get(target)!
          targetHandle = undefined
        }
        // Skip self-loops (both ends remapped to same group)
        if (source === target) return
        // Skip edges targeting group nodes — parent-child is via parentNode, not edges
        const targetYNode = yNodes.get(target)
        if (targetYNode) {
          const targetType = targetYNode.get('type') as string | undefined
          if (targetType === 'nodeGroup' || targetType === 'shortVideoGroup' || targetType === 'webtoonGroup') return
        }
        // Deduplicate edges that collapse to the same source→target
        const dedup = `${source}→${target}`
        if (seen.has(dedup)) return
        seen.add(dedup)

        const edge: Edge = { id: edgeId, source, target, type }
        if (sourceHandle) edge.sourceHandle = sourceHandle
        if (targetHandle) edge.targetHandle = targetHandle
        const toIndex = yedge.get('toIndex') as number | undefined
        if (toIndex !== undefined) {
          edge.data = { ...edge.data, toIndex }
        }
        edges.push(edge)
      })
      return edges
    }

    const yClientID = ydoc.clientID

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rebuild = (events?: Y.YEvent<any>[], source?: 'nodes' | 'edges'): void => {
      // During undo/redo, skip — the undo handler owns node state
      if (isUndoRedo.current) return

      const origin = events && events.length > 0 ? events[0].transaction.origin : 'initial'

      // Skip rebuild for our own frontend writes (e.g. config field edits).
      if (events && events.length > 0 && events[0].transaction.origin === 'frontend') {
        return
      }

      // --- Edge-only fast path ---
      // When only edges changed (add/remove/reorder), update edges without
      // touching nodes. Calling setNodes with new objects forces ReactFlow to
      // re-measure node dimensions, which briefly un-renders all edges.
      if (source === 'edges' && !isInitialBuild.current) {
        log('rebuild → EDGE-ONLY fast path (client=%d origin=%s)', yClientID, origin)
        setEdges(() => {
          const freshEdges = readEdgesFromYDoc()
          edgesRef.current = freshEdges
          return freshEdges
        })
        return
      }

      // --- Node data-only fast path (status/label/in_*/out_*) ---
      // Update React node data in place without rebuilding from scratch.
      // RFC-0319: per-field keys (in_*/out_*) are also non-structural data updates.
      const isNonStructuralKey = (key: string): boolean =>
        !YDOC_STRUCTURAL_KEYS.has(key) ||
        key === 'status' || key === 'label'
      const isDataOnlyUpdate = events && events.length > 0 && nodesRef.current.length > 0 && events.every(e => {
        if (!(e.target instanceof Y.Map)) return false
        const keys = e.changes?.keys as Map<string, { action: string }> | undefined
        if (!keys || keys.size === 0) return false
        for (const [key, change] of keys) {
          if (!isNonStructuralKey(key) || change.action !== 'update') return false
        }
        return true
      })

      // Debug: log status changes on any node
      if (events) {
        for (const e of events) {
          if (!(e.target instanceof Y.Map)) continue
          const keys = e.changes?.keys as Map<string, { action: string; oldValue?: unknown }> | undefined
          if (!keys) continue
          for (const [key, change] of keys) {
            if (key === 'status') {
              const ymap = e.target as Y.Map<unknown>
              const nodeId = [...yNodes.entries()].find(([, v]) => v === ymap)?.[0] ?? '??'
              const newVal = ymap.get('status')
              const nodeType = ymap.get('type') as string | undefined
              log('statusChange node=%s status: %s → %s (client=%d origin=%s)', nodeId, String(change.oldValue), String(newVal), yClientID, String(origin))
              statusLog('ydoc-change nodeId=%s nodeType=%s old=%s new=%s origin=%s', nodeId, nodeType ?? '??', String(change.oldValue), String(newVal), String(origin))
            }
          }
        }
      }

      // Count running and queued non-director nodes across ALL YDoc nodes (including canvas-hidden types).
      // Must run on every update (data-only AND full rebuild) so the task banner stays current.
      {
        let runCount = 0
        let queuedCount = 0
        const runningIds: string[] = []
        yNodes.forEach((ynode: Y.Map<unknown>, nodeId: string) => {
          const st = ynode.get('status') as string | undefined
          const tp = ynode.get('type') as string | undefined
          if (tp === 'director') return
          if (st === 'running') {
            runCount++
            runningIds.push(`${nodeId}(${tp})`)
          } else if (st === 'pending_execution') {
            queuedCount++
          }
        })
        log('runCount running=%d queued=%d %o', runCount, queuedCount, runningIds.length > 0 ? runningIds : '(none)')
        setYdocRunningCount(runCount)
        setYdocQueuedCount(queuedCount)
      }

      if (isDataOnlyUpdate) {
        log('rebuild → DATA-ONLY fast path (client=%d origin=%s)', yClientID, origin)
        // Use functional updater to avoid race with useEffect(() => nodesRef.current = nodes).
        // The useEffect can clobber nodesRef with stale React state between renders;
        // functional updaters always receive React's latest pending state.
        setNodes(prevNodes => {
          // Compare against visible YDoc nodes only — hidden types (director,
          // storyboardDesigner, etc.) live in YDoc but are never rendered.
          let visibleYNodeCount = 0
          yNodes.forEach((_ynode: Y.Map<unknown>, _nodeId: string) => {
            const nt = (_ynode.get('type') as string) ?? ''
            if (!CANVAS_HIDDEN_FE_TYPES.has(nt) && (!knownNodeTypes || knownNodeTypes.has(nt))) {
              visibleYNodeCount++
            }
          })
          if (prevNodes.length === visibleYNodeCount) {
            log('rebuild DATA-ONLY: count match (%d), merging in-place', prevNodes.length)
            // First pass: merge data from YDoc
            const updatedNodes = prevNodes.map(rfNode => {
              const ynode = yNodes.get(rfNode.id)
              if (!ynode) return rfNode

              // RFC-0319: read per-field keys with data_json fallback
              const dataRaw = readNodeDataFromYMap(ynode)

              const newStatus = (ynode.get('status') as string) ?? 'idle'
              const newLabel = (ynode.get('label') as string) ?? rfNode.id

              const merged: Node = {
                ...rfNode,
                data: {
                  ...rfNode.data,
                  ...dataRaw,
                  status: newStatus,
                  label: newLabel,
                },
              }

              return merged
            })

            // Second pass: sync child hidden state with parent collapse
            const groupCollapseMap = new Map<string, boolean>()
            for (const n of updatedNodes) {
              if (n.type === 'webtoonGroup' || n.type === 'nodeGroup') {
                groupCollapseMap.set(n.id, n.data?.in_collapsed === true)
              }
            }
            const finalNodes = updatedNodes.map(n => {
              if (n.parentNode && groupCollapseMap.has(n.parentNode)) {
                const parentCollapsed = groupCollapseMap.get(n.parentNode)!
                if (n.hidden !== parentCollapsed) {
                  return { ...n, hidden: parentCollapsed }
                }
              }
              return n
            })

            nodesRef.current = finalNodes
            return finalNodes
          }

          // Mismatch: prevNodes is stale — rebuild from YDoc so the update
          // is not silently dropped.
          log('rebuild DATA-ONLY: COUNT MISMATCH prevNodes=%d visibleYNodes=%d (total=%d) — falling back to full rebuild', prevNodes.length, visibleYNodeCount, yNodes.size)
          const rebuilt = applySelection(readNodesFromYDoc(), selectedNodeIdRef)
          nodesRef.current = rebuilt
          return rebuilt
        })

        _autoFocus(nodesRef.current)
        return
      }

      // --- Full rebuild path: structural node changes (add/remove nodes) ---
      // Use functional updaters that re-read YDoc at React flush time. When YDoc
      // fires rapid add/remove events, React queues each setNodes() call. Direct
      // calls like setNodes(snapshot) freeze state at event time, but by the time
      // React processes the queue earlier snapshots overwrite later ones — causing
      // the "oscillating node count" bug. Functional updaters always read the
      // latest YDoc state when React actually applies the update.

      if (!isInitialBuild.current) {
        log('rebuild → FULL REBUILD (structural change), yNodes.size=%d (client=%d origin=%s)', yNodes.size, yClientID, origin)
        setNodes((_prevNodes) => {
          const freshNodes = readNodesFromYDoc()
          console.log('[YCanvas] FULL REBUILD: freshNodes=%d prevNodes=%d origin=%s', freshNodes.length, _prevNodes.length, origin)

          if (freshNodes.length === 0 && isInitialBuild.current) return _prevNodes
          const result = applySelection(freshNodes, selectedNodeIdRef)

          nodesRef.current = result
          return result
        })
        setEdges(() => {
          const freshEdges = readEdgesFromYDoc()
          edgesRef.current = freshEdges
          return freshEdges
        })
        setReady(true)
      } else {
        isInitialBuild.current = false
        setNodes((prevNodes) => {
          const freshNodes = readNodesFromYDoc()
          console.log('[YCanvas] INITIAL BUILD: freshNodes=%d prevNodes=%d', freshNodes.length, prevNodes.length)
          lifecycleLog('initial-build nodeCount=%d prevCount=%d', freshNodes.length, prevNodes.length)
          nodesRef.current = freshNodes
          return freshNodes
        })
        setEdges([])

        requestAnimationFrame(() => {
          setEdges(() => {
            const freshEdges = readEdgesFromYDoc()
            edgesRef.current = freshEdges
            return freshEdges
          })
          setReady(true)
        })
      }

      _autoFocus(readNodesFromYDoc())
    }

    const _autoFocus = (nodes: Node[]) => {
      const runningNodes = nodes.filter(n => {
        const status = (n.data as { status?: string })?.status
        return status === 'running'
      })
      const currentRunningIds = new Set(runningNodes.map(n => n.id))
      const newlyRunning = runningNodes.filter(n => !prevRunningIdsRef.current.has(n.id))
      prevRunningIdsRef.current = currentRunningIds

      if (newlyRunning.length === 0 || !reactFlowInstance.current) return

      // Explicit focus_node intents take priority — skip auto-focus if one was
      // received recently so we don't override the user-facing pan animation.
      if (Date.now() - lastFocusIntentTs.current < 2000) return

      // Accumulate newly running nodes and debounce the fitView call.
      // During batch generation (e.g. 9 storyboard panels), this prevents
      // the viewport from jumping to each panel individually.
      pendingFocusNodesRef.current.push(...newlyRunning)

      if (autoFocusTimerRef.current) {
        clearTimeout(autoFocusTimerRef.current)
      }

      autoFocusTimerRef.current = setTimeout(() => {
        const pending = pendingFocusNodesRef.current
        pendingFocusNodesRef.current = []
        autoFocusTimerRef.current = null

        if (pending.length > 0 && reactFlowInstance.current) {
          reactFlowInstance.current.fitView({
            nodes: pending,
            padding: 0.15,
            duration: 400,
            maxZoom: 1,
            minZoom: 0.3,
          })
        }
      }, 500)
    }

    // Initial build + observe both maps with source tags
    rebuild()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onNodesChange = (events: Y.YEvent<any>[]) => rebuild(events, 'nodes')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onEdgesChange = (events: Y.YEvent<any>[]) => rebuild(events, 'edges')
    yNodes.observeDeep(onNodesChange)
    yEdges.observeDeep(onEdgesChange)

    // --- Focus intent observer ---
    const yFocus = ydoc.getMap('focus')
    const scheduleFitView = (nodeId: string, attempt: number) => {
      const target = nodesRef.current.find(n => n.id === nodeId)
      if (!target) {
        if (attempt < 3) {
          log('focus: node %s not in React state yet, retry %d/3', nodeId, attempt + 1)
          setTimeout(() => scheduleFitView(nodeId, attempt + 1), 500)
        } else {
          log('focus: node %s not found after 3 retries, giving up', nodeId)
        }
        return
      }
      const rf = reactFlowInstance.current
      if (!rf) {
        log('focus: no reactFlowInstance, skipping')
        return
      }

      // For child nodes (e.g. panels inside a storyboard group), focus on
      // the parent group to show the full context instead of jumping between
      // individual children. fitView resolves absolute positions through the
      // ReactFlow parent chain, avoiding the bug where relative positions
      // were treated as absolute by setCenter.
      const focusId = target.parentNode || nodeId
      log('focus: fitting view on %s (requested %s)', focusId, nodeId)
      rf.fitView({
        nodes: [{ id: focusId }],
        padding: 0.15,
        duration: 500,
        maxZoom: 1,
        minZoom: 0.3,
      })
    }

    let pendingFocusIntent: string | null = null
    let focusIntentTimer: ReturnType<typeof setTimeout> | null = null

    const onFocusChange = () => {
      const nodeId = yFocus.get('nodeId') as string | undefined
      if (!nodeId || !reactFlowInstance.current) {
        log('focus: observer fired but no nodeId or no reactFlowInstance')
        return
      }

      lastFocusIntentTs.current = Date.now()
      log('focus: intent received for node %s', nodeId)

      // Debounce: when multiple focus intents arrive rapidly (e.g. 9 panel
      // completions in a storyboard), only the last one triggers viewport
      // movement. This prevents the viewport from jumping 9 times.
      pendingFocusIntent = nodeId
      if (focusIntentTimer) {
        clearTimeout(focusIntentTimer)
      }
      focusIntentTimer = setTimeout(() => {
        const pending = pendingFocusIntent
        pendingFocusIntent = null
        focusIntentTimer = null
        if (pending) {
          scheduleFitView(pending, 0)
        }
      }, 500)
    }
    yFocus.observe(onFocusChange)

    return () => {
      console.log('[YCanvas] effect cleanup: detaching observers')
      yNodes.unobserveDeep(onNodesChange)
      yEdges.unobserveDeep(onEdgesChange)
      yFocus.unobserve(onFocusChange)
      yNodesRef.current = null
      if (autoFocusTimerRef.current) {
        clearTimeout(autoFocusTimerRef.current)
        autoFocusTimerRef.current = null
      }
      if (focusIntentTimer) {
        clearTimeout(focusIntentTimer)
        focusIntentTimer = null
      }
    }
  }, [ydoc, synced, isBackendConnected, setNodes, setEdges, nodesRef, edgesRef, isUndoRedo, reactFlowInstance, knownNodeTypes, selectedNodeIdRef])

  // --- Write helpers ---

  const writeNodePosition = useCallback((nodeId: string, x: number, y: number, tainted?: boolean) => {
    const yNodes = yNodesRef.current
    if (!yNodes) {
      console.warn('[writeNodePosition] yNodesRef is null, skipping', nodeId)
      return
    }

    const ynode = yNodes.get(nodeId)
    if (!ynode) {
      console.warn('[writeNodePosition] node not found in Y doc, skipping', nodeId)
      return
    }

    // Skip position writes for group children — their positions are computed
    // from in_child_node_ids order in readNodesFromYDoc, never stored.
    if (ynode.get('parentNode')) return

    log('writePosition node=%s x=%d y=%d tainted=%s (client=%d)', nodeId, x, y, tainted, ydoc.clientID)
    statusLog('position-write nodeId=%s x=%d y=%d source=drag', nodeId, x, y)
    const curX = ynode.get('x') as number | undefined
    const curY = ynode.get('y') as number | undefined
    if (curX === x && curY === y && (tainted === undefined || ynode.get('tainted') === tainted)) return
    ydoc.transact(() => {
      ynode.set('x', x)
      ynode.set('y', y)
      if (tainted !== undefined) {
        ynode.set('tainted', tainted)
      }
    }, 'frontend')
  }, [ydoc])

  const addNodeToYDoc = useCallback((
    nodeId: string,
    nodeType: string,
    label: string,
    x: number,
    y: number,
    data?: Record<string, unknown>,
    tainted?: boolean,
  ) => {
    const yNodes = yNodesRef.current
    if (!yNodes) {
      console.warn('[addNodeToYDoc] yNodesRef is null, skipping', nodeId)
      return
    }

    if (yNodes.has(nodeId)) {
      return
    }

    ydoc.transact(() => {
      const ynode = new Y.Map<unknown>()
      ynode.set('type', nodeType)
      ynode.set('label', label)
      ynode.set('status', 'idle')
      ynode.set('x', x)
      ynode.set('y', y)
      ynode.set('tainted', !!tainted)
      // Write initial data as per-field keys
      if (data) {
        const backendType = toBackendType(nodeType)
        const fieldSpecs = YDOC_NODE_FIELDS[backendType] ?? {}
        for (const [key, value] of Object.entries(data)) {
          if (YDOC_STRUCTURAL_KEYS.has(key)) continue
          // Skip File objects (e.g. initialFile on upload nodes) — not serializable
          if (typeof File !== 'undefined' && value instanceof File) continue
          const spec = fieldSpecs[key]
          if (spec?.wireType === 'array') {
            const arr = Array.isArray(value) ? value : (value != null ? [value] : [])
            const yArr = new Y.Array()
            yArr.push(arr)
            ynode.set(key, yArr)
          } else {
            ynode.set(key, serializeFieldValue(value))
          }
        }
      }
      yNodes.set(nodeId, ynode)
    }, 'frontend')
  }, [ydoc])

  const removeNodeFromYDoc = useCallback((nodeId: string) => {
    const yNodes = yNodesRef.current
    if (!yNodes || !yNodes.has(nodeId)) return
    ydoc.transact(() => {
      yNodes.delete(nodeId)
    }, 'frontend')
  }, [ydoc])

  const writeNodeData = useCallback((nodeId: string, updates: Record<string, unknown>) => {
    const yNodes = yNodesRef.current
    if (!yNodes) return

    const ynode = yNodes.get(nodeId)
    if (!ynode) return

    // Look up node type for wire-format-aware serialization
    const feType = (ynode.get('type') as string) || ''
    const fieldSpecs = YDOC_NODE_FIELDS[toBackendType(feType)] ?? {}

    ydoc.transact(() => {
      // Write all data fields to YDoc as per-field keys.
      // Wire-format-aware serialization for in_*/out_* fields with specs;
      // internal fields (batch_pending, etc.) use basic serialization.
      for (const [key, value] of Object.entries(updates)) {
        // Skip structural keys that are managed separately
        if (YDOC_STRUCTURAL_KEYS.has(key)) continue

        const spec = fieldSpecs[key]
        if (spec?.wireType === 'map') {
          const mapVal = value as Record<string, unknown> | null
          if (!mapVal) {
            ynode.set(key, serializeFieldValue(value))
            ydocWriteLog('write nodeId=%s key=%s wireType=map (null)', nodeId, key)
          } else if ('history' in mapVal) {
            // GeneratedImageWithHistory shape
            const historyMapVal = mapVal as { history?: unknown[]; picked_index?: number }
            const existing = ynode.get(key)
            if (existing instanceof Y.Map) {
              // Incremental: update sub-keys without replacing the whole map
              existing.set('picked_index', historyMapVal.picked_index ?? 0)
              const yArr = existing.get('history')
              if (yArr instanceof Y.Array && historyMapVal.history) {
                yArr.delete(0, yArr.length)
                yArr.push(historyMapVal.history.map(h => typeof h === 'string' ? h : JSON.stringify(h)))
              }
            } else {
              const yMap = new Y.Map()
              const yArr = new Y.Array()
              yArr.push((historyMapVal.history ?? []).map(h => typeof h === 'string' ? h : JSON.stringify(h)))
              yMap.set('history', yArr)
              yMap.set('picked_index', historyMapVal.picked_index ?? 0)
              ynode.set(key, yMap)
            }
            ydocWriteLog('write nodeId=%s key=%s wireType=map(history) historyLen=%d', nodeId, key, historyMapVal.history?.length ?? 0)
          } else {
            // Plain dict shape (e.g. in_resolved_voice_map, in_audio_selections)
            const existing = ynode.get(key)
            if (existing instanceof Y.Map) {
              // Update existing Y.Map: remove old keys, set new ones
              for (const oldKey of [...existing.keys()]) {
                if (!(oldKey in mapVal)) existing.delete(oldKey)
              }
              for (const [k, v] of Object.entries(mapVal)) {
                existing.set(k, v)
              }
            } else {
              const yMap = new Y.Map()
              for (const [k, v] of Object.entries(mapVal)) {
                yMap.set(k, v)
              }
              ynode.set(key, yMap)
            }
            ydocWriteLog('write nodeId=%s key=%s wireType=map(plain) keys=%d', nodeId, key, Object.keys(mapVal).length)
          }
        } else if (spec?.wireType === 'array') {
          // RFC-0609: Store as native Y.Array to preserve CRDT semantics
          const arr = Array.isArray(value) ? value : (value != null ? [value] : [])
          const existing = ynode.get(key)
          if (existing instanceof Y.Array) {
            existing.delete(0, existing.length)
            existing.push(arr)
          } else {
            const yArr = new Y.Array()
            yArr.push(arr)
            ynode.set(key, yArr)
          }
          ydocWriteLog('write nodeId=%s key=%s wireType=array len=%d', nodeId, key, arr.length)
        } else {
          const serialized = serializeFieldValue(value)
          if (ynode.get(key) === serialized) {
            ydocWriteLog('skip same-value nodeId=%s key=%s', nodeId, key)
            continue
          }
          ynode.set(key, serialized)
          ydocWriteLog('write nodeId=%s key=%s', nodeId, key)
        }
      }

      // Promote well-known fields to top-level YDoc keys
      if ('name' in updates) {
        const v = updates.name as string
        if (ynode.get('label') !== v) {
          ynode.set('label', v)
          ydocWriteLog('write nodeId=%s key=%s', nodeId, 'label (from name)')
        } else {
          ydocWriteLog('skip same-value nodeId=%s key=%s', nodeId, 'label (from name)')
        }
      }
      if ('label' in updates && typeof updates.label === 'string') {
        if (ynode.get('label') !== updates.label) {
          ynode.set('label', updates.label)
          ydocWriteLog('write nodeId=%s key=%s', nodeId, 'label')
        } else {
          ydocWriteLog('skip same-value nodeId=%s key=%s', nodeId, 'label')
        }
      }
      if ('status' in updates && typeof updates.status === 'string') {
        if (ynode.get('status') !== updates.status) {
          ynode.set('status', updates.status)
          ydocWriteLog('write nodeId=%s key=%s', nodeId, 'status')
        } else {
          ydocWriteLog('skip same-value nodeId=%s key=%s', nodeId, 'status')
        }
      }
    }, 'frontend')
  }, [ydoc])

  const writeNodeDimensions = useCallback((nodeId: string, width: number, height: number) => {
    const yNodes = yNodesRef.current
    if (!yNodes) return

    const ynode = yNodes.get(nodeId)
    if (!ynode) return

    // Skip backend-managed group types — their dimensions are computed by the backend.
    const ynodeType = ynode.get('type') as string
    if (ynodeType === 'webtoonGroup' || ynodeType === 'shortVideoGroup') return

    // Round to integers to prevent sub-pixel jitter loops
    const w = Math.round(width)
    const h = Math.round(height)
    const curW = ynode.get('width') as number | undefined
    const curH = ynode.get('height') as number | undefined
    if (curW === w && curH === h) return

    layoutLog('[size] writeNodeDimensions: %s type=%s w=%d h=%d prev_w=%s prev_h=%s',
      nodeId, ynodeType, w, h, curW, curH)

    ydoc.transact(() => {
      ynode.set('width', w)
      ynode.set('height', h)
    }, 'frontend')
  }, [ydoc])

  const writeNodeParent = useCallback((nodeId: string, parentId: string | null) => {
    const yNodes = yNodesRef.current
    if (!yNodes) return

    const ynode = yNodes.get(nodeId)
    if (!ynode) return

    ydoc.transact(() => {
      if (parentId) {
        ynode.set('parentNode', parentId)
      } else {
        ynode.delete('parentNode')
      }
    }, 'frontend')
  }, [ydoc])

  // Cross-instance node status sync: listen for dw:node-status events
  // dispatched by the chat WebSocket handler (via Redis Pub/Sub) and apply
  // the status directly to the local YDoc so the canvas updates.
  useEffect(() => {
    const handler = (e: Event) => {
      const { nodeId, status } = (e as CustomEvent).detail
      const yNodes = yNodesRef.current
      if (!yNodes) return
      const ynode = yNodes.get(nodeId)
      if (!ynode) return
      if (ynode.get('status') === status) return
      ydoc.transact(() => {
        ynode.set('status', status)
      }, 'backend-relay')
    }
    window.addEventListener('dw:node-status', handler)
    return () => window.removeEventListener('dw:node-status', handler)
  }, [ydoc])

  return { ready, ydocRunningCount, ydocQueuedCount, writeNodePosition, addNodeToYDoc, removeNodeFromYDoc, writeNodeData, writeNodeDimensions, writeNodeParent }
}

// ---------------------------------------------------------------------------
// useYViewport: Y doc → Viewport + write helper
// ---------------------------------------------------------------------------

interface UseYViewportResult {
  /** Viewport from the Y document (null until synced) */
  viewport: Viewport | null
  /** Write viewport changes back to the Y document */
  writeViewport: (viewport: Viewport) => void
}

export function useYViewport(ydoc: Y.Doc, synced: boolean): UseYViewportResult {
  const [viewport, setViewport] = useState<Viewport | null>(null)
  const yViewportRef = useRef<Y.Map<number> | null>(null)
  const suppressRef = useRef(false)

  useEffect(() => {
    if (!synced) return

    const yViewport = ydoc.getMap('viewport') as Y.Map<number>
    yViewportRef.current = yViewport

    const rebuild = () => {
      if (suppressRef.current) {
        suppressRef.current = false
        return
      }
      const x = yViewport.get('x') ?? 0
      const y = yViewport.get('y') ?? 0
      const zoom = yViewport.get('zoom') ?? 1
      setViewport({ x, y, zoom })
    }

    rebuild()
    yViewport.observeDeep(rebuild)

    return () => {
      yViewport.unobserveDeep(rebuild)
      yViewportRef.current = null
    }
  }, [ydoc, synced])

  const writeViewport = useCallback((vp: Viewport) => {
    const yViewport = yViewportRef.current
    if (!yViewport) return

    suppressRef.current = true
    ydoc.transact(() => {
      yViewport.set('x', vp.x)
      yViewport.set('y', vp.y)
      yViewport.set('zoom', vp.zoom)
    }, 'frontend')
  }, [ydoc])

  return { viewport, writeViewport }
}
